import { SignupFlowSlug, flowPages } from "../constants";
import { useSignupFlowStore } from "../hooks/useSignUpFlowStore";

import type { PrefilledFieldsStoreState } from "./validatePrefilledFields";
import {
  validatePrefilledFields,
  resolveFieldValue,
} from "./validatePrefilledFields";
import { isFieldPopulated } from "./isFieldPopulated";
import { requiredFieldsBySlug } from "./requiredFieldsBySlug";
import { findFirstIncompleteSignupPage } from "./findFirstIncompleteSignupPage";
import {
  getFlowNavigationContextFromStore,
} from "../navigation/getFlowNavigationContextFromStore";
import { getNextPageSlug } from "../navigation/getNextPageSlug";
import { isPageAccessible } from "../navigation/isPageAccessible";

const START_SLUG = SignupFlowSlug.CAT_COUNT;

export interface RehydrationStoreState extends PrefilledFieldsStoreState {
  versionMismatchDetected: boolean;
}

export interface RehydrationRedirectOptions {
  /** The slug the user navigated to directly. When provided, fields are
   *  checked before this slug (or the next accessible page if it is
   *  inaccessible) and the RECIPES/CHECKOUT fallback is skipped. */
  targetSlug?: SignupFlowSlug;
}

type RehydrationRedirectReason =
  | "version_mismatch"
  | "incomplete"
  | "target_inaccessible"
  | "resume"
  | "none";

interface RehydrationRedirect {
  slug: SignupFlowSlug | null;
  /** `resume`: nothing is incomplete, so the user resumes at RECIPES or CHECKOUT. */
  reason: RehydrationRedirectReason;
}

// User recognised as having reached recipes previously if they have an email stored.
function hasReachedRecipes(state: PrefilledFieldsStoreState): boolean {
  return Boolean(state.user?.email);
}

// Checkout fields that are populated before checkout (email) or without user
// input (defaults), so they don't indicate that the user reached checkout.
const NON_CHECKOUT_SIGNAL_FIELDS = new Set<string>([
  "email",
  "deliveryDate",
  "billingAddress.countryCode",
]);

// User recognised as having reached checkout if any checkout field they
// fill in themselves is populated in the store.
function hasReachedCheckout(state: PrefilledFieldsStoreState): boolean {
  const checkoutFields = requiredFieldsBySlug[SignupFlowSlug.CHECKOUT]({})
    .filter((field) => !NON_CHECKOUT_SIGNAL_FIELDS.has(field));
  return checkoutFields.some((field) =>
    isFieldPopulated(resolveFieldValue(field, state)),
  );
}

/**
 * Determines where a returning user should be redirected after store
 * rehydration (from localStorage or an AC API response).
 *
 * Version mismatch → start of the flow, for either entry type.
 *
 * **Direct-link entry** (`targetSlug` = the page the user navigated to):
 * - Target accessible → first critical incomplete before `targetSlug`,
 *   or null (stay on the page).
 * - Target inaccessible (e.g. skipped in an A/B test group) → first critical
 *   incomplete before the next accessible page, or that next accessible page
 *   (redirecting there). Falls back to the start of the flow when no
 *   accessible page follows.
 *
 * **CTA / AC entry** (no `targetSlug`):
 * - Reached recipes → first critical incomplete before RECIPES,
 *   or CHECKOUT (if checkout data exists), or RECIPES.
 * - Not reached recipes → first incomplete (any) before RECIPES.
 */
export function getRehydrationRedirect(
  storeState?: RehydrationStoreState,
  options?: RehydrationRedirectOptions,
): RehydrationRedirect {
  const state = storeState ?? useSignupFlowStore.getState();

  if (state.versionMismatchDetected) {
    return { slug: START_SLUG, reason: "version_mismatch" };
  }

  if (options?.targetSlug) {
    return getDirectEntryRedirect(state, options.targetSlug);
  }

  return getCtaEntryRedirect(state);
}

function toRedirect(slug: SignupFlowSlug | null): RehydrationRedirect {
  return slug
    ? { slug, reason: "incomplete" }
    : { slug: null, reason: "none" };
}

function getDirectEntryRedirect(
  state: PrefilledFieldsStoreState,
  targetSlug: SignupFlowSlug,
): RehydrationRedirect {
  const navigationContext = getFlowNavigationContextFromStore();
  const isTargetAccessible = isPageAccessible(targetSlug, navigationContext);
  const destinationSlug = isTargetAccessible
    ? targetSlug
    : getNextPageSlug(targetSlug, navigationContext, flowPages) ?? START_SLUG;

  validatePrefilledFields(state, destinationSlug);
  const firstIncomplete = findFirstIncompleteSignupPage(
    destinationSlug,
    { criticalOnly: true },
  );
  if (firstIncomplete) {
    return toRedirect(firstIncomplete);
  }

  return isTargetAccessible
    ? { slug: null, reason: "none" }
    : { slug: destinationSlug, reason: "target_inaccessible" };
}

function getCtaEntryRedirect(
  state: PrefilledFieldsStoreState,
): RehydrationRedirect {
  validatePrefilledFields(state, SignupFlowSlug.RECIPES);

  if (!hasReachedRecipes(state)) {
    return toRedirect(findFirstIncompleteSignupPage(SignupFlowSlug.RECIPES, {}));
  }

  const firstIncomplete = findFirstIncompleteSignupPage(
    SignupFlowSlug.RECIPES,
    { criticalOnly: true },
  );
  if (firstIncomplete) {
    return toRedirect(firstIncomplete);
  }

  return {
    slug: hasReachedCheckout(state) ? SignupFlowSlug.CHECKOUT : SignupFlowSlug.RECIPES,
    reason: "resume",
  };
}
